/**
 * LicensesPage — Reminders > Licenses/Permits/Reminders.
 * Structure mirrors EinPage. Attachments are page-global sections (managed
 * via /api/licenses/attachment-sections) rather than a hard-coded list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, Archive, Paperclip, X, List as ListIcon, CaretUp, CaretDown, ArrowClockwise, FileXls, PencilSimple, Bell } from "@phosphor-icons/react";
import LicenseModal from "@/components/licenses/LicenseModal";
import AddColumnModal from "@/components/shared/AddColumnModal";
import RestoreColumnsModal from "@/components/shared/RestoreColumnsModal";
import BulkUploadModal from "@/components/shared/BulkUploadModal";
import ReminderConfigModal from "@/components/shared/ReminderConfigModal";

const ITEM_TYPES = ["License", "Permit", "Reminder"];

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(String(s).length <= 10 ? String(s) + "T00:00:00" : String(s));
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};
const fmtLU = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d)) return "";
  return `${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.${String(d.getFullYear()).slice(-2)}`;
};

function expiryTone(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).length <= 10 ? String(iso) + "T00:00:00" : String(iso));
  if (Number.isNaN(d.getTime())) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const diff = Math.round((d - t) / 86400000);
  if (diff < 0)  return { cls: "bg-rose-600 text-white",                                 rowCls: "bg-rose-100/70",   label: `${Math.abs(diff)}d overdue` };
  if (diff <=30) return { cls: "bg-rose-100 text-rose-700 border border-rose-200",       rowCls: "bg-rose-50/60",    label: `${diff}d` };
  if (diff <=60) return { cls: "bg-orange-100 text-orange-700 border border-orange-200", rowCls: "bg-orange-50/50",  label: `${diff}d` };
  if (diff <=90) return { cls: "bg-yellow-100 text-yellow-800 border border-yellow-200", rowCls: "bg-yellow-50/40",  label: `${diff}d` };
  return { cls: "text-slate-600", rowCls: "", label: `${diff}d` };
}

export default function LicensesPage() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const [newRow, setNewRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [addColOpen, setAddColOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [reminderCfgOpen, setReminderCfgOpen] = useState(false);
  const [reminderMode, setReminderMode] = useState(false);
  const [customCols, setCustomCols] = useState([]);
  const [sections, setSections] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/licenses/access"); setAccess(data); }
      catch { setAccess({ can_view: false }); }
    })();
  }, []);

  const load = useCallback(async () => {
    try {
      const [a, d] = await Promise.all([api.get("/licenses"), api.get("/licenses?deleted=true")]);
      setItems([...(a.data || []), ...(d.data || [])]);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  }, []);
  const loadCustomCols = useCallback(async () => {
    try { const { data } = await api.get("/licenses/columns"); setCustomCols(Array.isArray(data) ? data : []); }
    catch { setCustomCols([]); }
  }, []);
  const loadSections = useCallback(async () => {
    try { const { data } = await api.get("/licenses/attachment-sections"); setSections(Array.isArray(data) ? data : []); }
    catch { setSections([]); }
  }, []);
  const loadItemTypes = useCallback(async () => {
    try { const { data } = await api.get("/licenses/item-types"); setItemTypes(Array.isArray(data) ? data : []); }
    catch { setItemTypes([]); }
  }, []);
  useEffect(() => { if (access?.can_view) { load(); loadCustomCols(); loadSections(); loadItemTypes(); } }, [access?.can_view, load, loadCustomCols, loadSections, loadItemTypes]);

  const active = useMemo(() => items.filter((x) => !x.archived && !x.deleted), [items]);
  const archived = useMemo(() => items.filter((x) => x.archived && !x.deleted), [items]);
  const deleted = useMemo(() => items.filter((x) => x.deleted), [items]);

  const filter = useCallback((list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((x) => JSON.stringify(x).toLowerCase().includes(q));
  }, [search]);

  const kpi = useMemo(() => {
    const c = { total: active.length, licenses: 0, permits: 0, reminders: 0 };
    active.forEach((x) => {
      if (x.item_type === "License") c.licenses++;
      else if (x.item_type === "Permit") c.permits++;
      else if (x.item_type === "Reminder") c.reminders++;
    });
    return c;
  }, [active]);

  const nextExpiration = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return active.filter((x) => x.expiration_date)
      .map((x) => ({ ...x, _d: new Date(x.expiration_date + "T00:00:00") }))
      .filter((x) => !Number.isNaN(x._d) && x._d >= today)
      .sort((a, b) => a._d - b._d)[0] || null;
  }, [active]);

  const startAdd = () => setNewRow({ item_type: "License", item_name: "", start_date: null, expiration_date: null });

  const saveNew = async () => {
    if (!newRow || !newRow.item_name?.trim()) { toast.error("Item Name required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/licenses", newRow);
      toast.success(`Item #${data.item_seq} added`);
      setItems((prev) => [...prev, data]);
      setNewRow(null);
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
    finally { setSaving(false); }
  };

  const toggleArchive = async (x) => {
    try { const { data } = await api.patch(`/licenses/${x.id}`, { field: "archived", value: !x.archived });
      setItems((prev) => prev.map((y) => y.id === x.id ? data : y));
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const toggleReminderFlag = async (x) => {
    try { const { data } = await api.patch(`/licenses/${x.id}`, { field: "email_reminder_enabled", value: !x.email_reminder_enabled });
      setItems((prev) => prev.map((y) => y.id === x.id ? data : y));
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  // Combine hard-coded defaults with DB-known types so previously-added
  // free-text types (e.g. "Biennial") surface as suggestions too.
  const itemTypeSuggestions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of ITEM_TYPES) { const k = t.toLowerCase(); if (!seen.has(k)) { seen.add(k); out.push(t); } }
    for (const it of itemTypes) {
      const v = String(it.value || "").trim();
      if (!v) continue;
      const k = v.toLowerCase();
      if (!seen.has(k)) { seen.add(k); out.push(v); }
    }
    return out;
  }, [itemTypes]);
  const softDelete = async (x) => {
    if (!window.confirm(`Move item #${x.item_seq} to Deleted?`)) return;
    try { await api.delete(`/licenses/${x.id}`); setItems((p) => p.map((y) => y.id === x.id ? { ...y, deleted: true } : y)); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const restoreDeleted = async (x) => {
    try { const { data } = await api.patch(`/licenses/${x.id}`, { field: "deleted", value: false });
      setItems((p) => p.map((y) => y.id === x.id ? data : y));
    } catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };
  const hardDelete = async (x) => {
    if (!window.confirm(`Purge item #${x.item_seq}?`)) return;
    try { await api.delete(`/licenses/${x.id}?hard=true`); setItems((p) => p.filter((y) => y.id !== x.id)); }
    catch (e) { const m = apiErrorMessage(e); if (m) toast.error(m); }
  };

  if (access === null) return <div className="min-h-screen bg-[#F4F6F8]"><AppHeader /><div className="p-8 text-center text-sm text-slate-500">Loading…</div></div>;
  if (!access?.can_view) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      <AppHeader />
      <main className="max-w-none mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10 py-3" data-testid="licenses-page">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <h1 className="text-xl font-bold text-slate-900 mr-2" data-testid="licenses-title">Licenses / Permits / Reminders</h1>
          <div className="relative flex-1 min-w-0 max-w-[520px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items…" className="pl-9 bg-white" data-testid="licenses-search" />
          </div>
          <div className="ml-auto flex items-center gap-1.5 flex-wrap">
            <Button size="sm" onClick={startAdd} className="bg-[#1f64c2] hover:bg-[#1854a3]" data-testid="licenses-add">
              <Plus size={13} className="mr-1" /> Add Item
            </Button>
            <Button size="sm" variant="outline" onClick={() => setToolbarOpen((s) => !s)} data-testid="licenses-toolbar-toggle">
              <ListIcon size={14} className="mr-1" />
              {toolbarOpen ? <CaretUp size={11} /> : <CaretDown size={11} />}
            </Button>
          </div>
        </div>

        {toolbarOpen && (
          <div className="mb-3 rounded-lg border border-slate-200 bg-white p-2 flex flex-wrap gap-1.5" data-testid="licenses-toolbar-panel">
            <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="licenses-bulk-open">
              <FileXls size={14} className="mr-1.5" /> Bulk upload
            </Button>
            <Button size="sm" variant="outline" onClick={startAdd} data-testid="licenses-toggle-edit">
              <PencilSimple size={14} className="mr-1.5" /> Add / edit mode
            </Button>
            <Button size="sm" variant={reminderMode ? "default" : "outline"} onClick={() => setReminderMode((m) => !m)}
              data-testid="licenses-reminder-mode"
              className={reminderMode ? "bg-amber-500 hover:bg-amber-600" : ""}>
              <Bell size={14} className="mr-1.5" /> {reminderMode ? "Exit reminder mode" : "Reminder flags"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setReminderCfgOpen(true)} data-testid="licenses-email-reminders">
              <Bell size={14} className="mr-1.5" /> Email reminders
            </Button>
          </div>
        )}

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 mb-3" data-testid="licenses-kpis">
          <KpiCard label="Total Items" value={kpi.total} testId="kpi-total" />
          <KpiCard label="Total Licenses" value={kpi.licenses} testId="kpi-licenses" />
          <KpiCard label="Total Permits" value={kpi.permits} testId="kpi-permits" />
          <KpiCard label="Total Reminders" value={kpi.reminders} testId="kpi-reminders" />
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid="kpi-next-expiration">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Next Expiration</div>
            <div className="text-lg font-bold text-slate-900 leading-tight mt-0.5">{nextExpiration ? fmtDate(nextExpiration.expiration_date) : "—"}</div>
            {nextExpiration && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{nextExpiration.item_type} · {nextExpiration.item_name}</div>}
          </div>
          <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-[10px]" data-testid="expiration-legend">
            <div className="uppercase tracking-widest text-slate-500 font-semibold mb-1">Expiration Legend</div>
            <div className="flex flex-wrap gap-1">
              <span className="bg-rose-100 text-rose-700 border border-rose-200 px-1.5 py-0.5 rounded-full font-semibold">≤30 d</span>
              <span className="bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded-full font-semibold">≤60 d</span>
              <span className="bg-yellow-100 text-yellow-800 border border-yellow-200 px-1.5 py-0.5 rounded-full font-semibold">≤90 d</span>
              <span className="bg-rose-600 text-white px-1.5 py-0.5 rounded-full font-semibold">Expired</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <SectionHeader label={`Active items (${filter(active).length}${search ? ` of ${active.length}` : ""})`} />
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setAddColOpen(true)} className="text-[11px] h-7 px-2" data-testid="licenses-add-column">
              <Plus size={11} className="mr-1" /> Add column
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRestoreOpen(true)} className="text-[11px] h-7 px-2" data-testid="licenses-restore-columns">
              <ArrowClockwise size={11} className="mr-1" /> Restore custom columns
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="licenses-table-active">
            <thead>
              <tr className="bg-slate-50">
                {reminderMode && <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b text-center">Flag</th>}
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b">#</th>
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b">Item Type</th>
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b">Item Name</th>
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b text-center">Attach.</th>
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b">Start Date</th>
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b">Expiration Date</th>
                {customCols.map((c) => <th key={c.id} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b whitespace-nowrap">{c.label}</th>)}
                <th className="text-[10px] uppercase tracking-widest font-bold text-slate-500 px-2 py-1.5 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {newRow && (
                <tr className="bg-[#eaf2ff]/40" data-testid="licenses-new-row">
                  {reminderMode && <td></td>}
                  <td className="px-2 py-0.5 border-b text-[11px] font-bold text-slate-500">NEW</td>
                  <td className="px-1.5 py-0.5 border-b">
                    <input list="license-item-type-options" value={newRow.item_type} onChange={(e) => setNewRow({ ...newRow, item_type: e.target.value })}
                      placeholder="License / Permit / …" className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs" data-testid="licenses-new-item_type" />
                    <datalist id="license-item-type-options">
                      {itemTypeSuggestions.map((t) => <option key={t} value={t} />)}
                    </datalist>
                  </td>
                  <td className="px-1.5 py-0.5 border-b"><input type="text" value={newRow.item_name} onChange={(e) => setNewRow({ ...newRow, item_name: e.target.value })} className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs" data-testid="licenses-new-item_name" /></td>
                  <td></td>
                  <td className="px-1.5 py-0.5 border-b"><input type="date" value={newRow.start_date || ""} onChange={(e) => setNewRow({ ...newRow, start_date: e.target.value || null })} className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs" /></td>
                  <td className="px-1.5 py-0.5 border-b"><input type="date" value={newRow.expiration_date || ""} onChange={(e) => setNewRow({ ...newRow, expiration_date: e.target.value || null })} className="w-full px-1 py-0.5 border border-slate-300 rounded text-xs" /></td>
                  {customCols.map((c) => <td key={c.id}></td>)}
                  <td className="px-2 py-0.5 border-b">
                    <Button size="sm" onClick={saveNew} disabled={saving} className="h-6 px-2 text-[10px] bg-[#327de3] hover:bg-[#1f64c2]" data-testid="licenses-new-save">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setNewRow(null)} className="h-6 px-2 text-[10px]"><X size={11} /></Button>
                  </td>
                </tr>
              )}
              {filter(active).map((x) => <ItemRow key={x.id} x={x} customCols={customCols} reminderMode={reminderMode} onToggleReminder={() => toggleReminderFlag(x)} onOpen={() => setOpenItem(x)} onArchive={() => toggleArchive(x)} />)}
              {filter(active).length === 0 && !newRow && (
                <tr><td colSpan={6 + customCols.length + 1 + (reminderMode ? 1 : 0)} className="text-center text-slate-400 italic py-6 text-xs">{search ? "No items match." : "No items yet."}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <button onClick={() => setShowArchived((s) => !s)} className="text-[11px] uppercase tracking-widest font-bold text-slate-500 inline-flex items-center gap-1" data-testid="licenses-archived-toggle">
            <span className={`inline-block transition-transform ${showArchived ? "rotate-90" : ""}`}>▸</span>
            Archived items ({filter(archived).length})
          </button>
          {showArchived && (
            <div className="mt-1 rounded-lg border bg-slate-50/60 overflow-x-auto">
              <table className="w-full text-xs" data-testid="licenses-table-archived">
                <tbody>
                  {filter(archived).map((x) => <ItemRow key={x.id} x={x} customCols={customCols} reminderMode={reminderMode} onToggleReminder={() => toggleReminderFlag(x)} dim onOpen={() => setOpenItem(x)} onArchive={() => toggleArchive(x)} onDelete={() => softDelete(x)} />)}
                  {filter(archived).length === 0 && <tr><td className="text-center text-slate-400 italic py-4 text-xs">No archived items.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-3">
          <button onClick={() => setShowDeleted((s) => !s)} className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 inline-flex items-center gap-1" data-testid="licenses-deleted-toggle">
            <span className={`inline-block transition-transform ${showDeleted ? "rotate-90" : ""}`}>▸</span>
            Deleted items ({filter(deleted).length})
          </button>
          {showDeleted && (
            <div className="mt-1 rounded-md border bg-slate-50/40 overflow-x-auto">
              <table className="w-full text-[10px]">
                <tbody>
                  {filter(deleted).map((x) => (
                    <tr key={x.id} className="opacity-60">
                      <td className="px-1.5 py-0.5 border-b font-semibold text-slate-500">#{x.item_seq}</td>
                      <td className="px-1.5 py-0.5 border-b">{x.item_type}</td>
                      <td className="px-1.5 py-0.5 border-b">{x.item_name}</td>
                      <td className="px-1.5 py-0.5 border-b">
                        <button onClick={() => restoreDeleted(x)} className="text-[9px] font-semibold text-emerald-700 hover:underline mr-1">Restore</button>
                        {user?.is_super_admin && <button onClick={() => hardDelete(x)} className="text-[9px] font-semibold text-rose-600 hover:underline">Purge</button>}
                      </td>
                    </tr>
                  ))}
                  {filter(deleted).length === 0 && <tr><td className="text-center text-slate-400 italic py-2 text-[10px]">No deleted items.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <LicenseModal
        item={openItem}
        customCols={customCols}
        sections={sections}
        itemTypeSuggestions={itemTypeSuggestions}
        onSectionsChange={loadSections}
        onClose={() => { setOpenItem(null); load(); loadItemTypes(); }}
        onChange={(u) => { setItems((prev) => prev.map((y) => y.id === u.id ? u : y)); setOpenItem(u); loadItemTypes(); }}
      />

      <AddColumnModal
        open={addColOpen} onClose={() => setAddColOpen(false)} onCreated={() => loadCustomCols()}
        basePath="/licenses"
        defaultLabels={["#", "Item Type", "Item Name", "Attach.", "Start Date", "Expiration Date"]}
        testIdPrefix="licenses-add-column"
      />
      <RestoreColumnsModal
        open={restoreOpen} onClose={() => setRestoreOpen(false)} onRestored={() => loadCustomCols()}
        basePath="/licenses" testIdPrefix="licenses-restore-columns"
      />
      <BulkUploadModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onImported={() => { load(); loadItemTypes(); }}
        basePath="/licenses"
        entityLabel="items"
        templateFilename="STRATA LICENSES TEMPLATE.xlsx"
        templateCols={[
          { key: "item_type", label: "Item Type", sample: "License" },
          { key: "item_name", label: "Item Name", sample: "NYC General Contractor" },
          { key: "start_date", label: "Start Date", date: true, sample: "2025-01-01" },
          { key: "expiration_date", label: "Expiration Date", date: true, sample: "2026-12-31" },
        ]}
        testIdPrefix="licenses-bulk"
      />
      <ReminderConfigModal
        open={reminderCfgOpen}
        onClose={() => setReminderCfgOpen(false)}
        isSuperAdmin={!!user?.is_super_admin}
        basePath="/licenses"
        entityLabel="items"
        entityCountField="flagged_items"
        testIdPrefix="licenses-reminder-config"
      />
    </div>
  );
}

function KpiCard({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-bold text-slate-900 leading-tight mt-0.5">{value}</div>
    </div>
  );
}

function SectionHeader({ label }) {
  return <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 mt-1">{label}</div>;
}

function ItemRow({ x, customCols = [], reminderMode, onToggleReminder, dim, onOpen, onArchive, onDelete }) {
  const tone = expiryTone(x.expiration_date);
  const rowCls = !x.archived && tone ? tone.rowCls : "";
  const cf = x.custom_fields || {};
  return (
    <tr className={`hover:bg-[#eaf2ff]/40 cursor-pointer border-b ${dim ? "opacity-70" : ""} ${x.archived ? "bg-emerald-50/70" : rowCls}`}
        onClick={onOpen} data-testid={`licenses-row-${x.id}`}>
      {reminderMode && (
        <td className="px-2 py-0.5 text-center" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={!!x.email_reminder_enabled} onChange={onToggleReminder}
            className="w-3.5 h-3.5 accent-amber-500" data-testid={`licenses-reminder-${x.id}`} />
        </td>
      )}
      <td className="px-2 py-0.5 font-bold text-slate-700 whitespace-nowrap">#{x.item_seq}</td>
      <td className="px-2 py-0.5 whitespace-nowrap">{x.item_type || ""}</td>
      <td className="px-2 py-0.5 whitespace-nowrap">{x.item_name || ""}</td>
      <td className="px-2 py-0.5 whitespace-nowrap text-center">
        {x.has_attachments ? <Paperclip size={13} weight="bold" className="inline text-slate-600" /> : <span className="inline-block w-5 h-3 bg-rose-200 border border-rose-300 rounded-sm" />}
      </td>
      <td className="px-2 py-0.5 whitespace-nowrap">{fmtDate(x.start_date)}</td>
      <td className="px-2 py-0.5 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <span>{fmtDate(x.expiration_date)}</span>
          {tone && <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${tone.cls}`}>{tone.label}</span>}
        </div>
      </td>
      {customCols.map((cc) => <td key={cc.id} className="px-2 py-0.5 whitespace-nowrap text-slate-700">{cf[cc.key] ?? ""}</td>)}
      <td className="px-2 py-0.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <button onClick={onArchive}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${x.archived ? "bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-white border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-600"}`}
            data-testid={`licenses-archive-${x.id}`}>
            <Archive size={10} className="inline mr-0.5" />{x.archived ? "Restore" : "Archive"}
          </button>
          {x.archived && onDelete && (
            <button onClick={onDelete} className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-white border-slate-200 text-slate-500 hover:border-rose-400 hover:text-rose-600" data-testid={`licenses-delete-${x.id}`}>Delete</button>
          )}
        </div>
      </td>
    </tr>
  );
}

export { fmtDate as _fmt };
